import { useNavigate } from "react-router-dom";
import { SlidersHorizontal, Download, RotateCcw, XCircle, Trash2, FileText } from "lucide-react";
import { StatusBadge } from "@/components/common/StatusBadge";
import { authedUrl } from "@/lib/api";
import { fmtDuration, fmtDate, fmtSecs, fmtCost } from "@/lib/format";

export const JobsTable = ({ jobs, onAction }) => {
  const nav = useNavigate();
  if (!jobs.length) return <div data-testid="jobs-empty-state" className="panel p-10 text-center text-slate-500 text-sm">No jobs yet. Send a master to separation from the Media Library.</div>;
  return (
    <div className="panel overflow-x-auto">
      <table data-testid="jobs-table" className="w-full text-sm">
        <thead>
          <tr className="text-left micro border-b border-[#253044]">
            <th className="px-3 py-2.5">Status</th>
            <th className="px-3 py-2.5">Source</th>
            <th className="px-3 py-2.5">Preset</th>
            <th className="px-3 py-2.5">Engine</th>
            <th className="px-3 py-2.5">Model</th>
            <th className="px-3 py-2.5">Duration</th>
            <th className="px-3 py-2.5">Proc. time</th>
            <th className="px-3 py-2.5">Cost</th>
            <th className="px-3 py-2.5">Stems</th>
            <th className="px-3 py-2.5">QC</th>
            <th className="px-3 py-2.5">Queued</th>
            <th className="px-3 py-2.5 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.id} data-testid={`job-row-${j.id}`} className="row-hover border-b border-[#1c2433] last:border-0">
              <td className="px-3 py-2.5">
                <StatusBadge status={j.status} progress={j.progress} testId={`job-status-${j.id}`} />
                {j.status === "processing" && <div className="text-[10px] text-slate-500 mt-1 font-mono truncate max-w-[160px]">{j.stage}</div>}
              </td>
              <td className="px-3 py-2.5">
                <div className="font-medium text-slate-100 truncate max-w-[220px]" title={j.source_filename}>{j.source_filename}</div>
                <div className="text-[11px] text-slate-500 font-mono">{j.project_id || "—"} / {j.episode_id || "—"}{j.benchmark_id ? " · benchmark" : ""}</div>
              </td>
              <td className="px-3 py-2.5 font-mono text-xs">{j.preset}</td>
              <td className="px-3 py-2.5 text-xs">{j.engine_name || j.engine}</td>
              <td className="px-3 py-2.5 font-mono text-[11px] text-slate-400 max-w-[180px] truncate" title={`${j.model_name} ${j.model_version}`}>{j.model_name}</td>
              <td className="px-3 py-2.5 font-mono text-xs">{fmtDuration(j.source_duration)}</td>
              <td className="px-3 py-2.5 font-mono text-xs">{fmtSecs(j.processing_duration_sec)}</td>
              <td className="px-3 py-2.5 font-mono text-xs">{fmtCost(j.processing_cost, j.cost_unit)}</td>
              <td className="px-3 py-2.5 font-mono text-xs">{Object.keys(j.outputs || {}).length}/{j.requested_stems.length}</td>
              <td className="px-3 py-2.5"><StatusBadge status={j.qc_status} testId={`job-qc-${j.id}`} /></td>
              <td className="px-3 py-2.5 text-xs text-slate-400">{fmtDate(j.queued_at)}</td>
              <td className="px-3 py-2.5">
                <div className="flex justify-end gap-1">
                  {j.status === "completed" && (
                    <>
                      <button data-testid={`job-open-qc-${j.id}`} onClick={() => nav(`/qc/${j.id}`)} className="flex items-center gap-1 px-2.5 py-1 rounded-sm bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25 text-xs font-medium"><SlidersHorizontal size={12} /> QC</button>
                      <a data-testid={`job-download-zip-${j.id}`} href={authedUrl(`/jobs/${j.id}/download-all`)} className="flex items-center gap-1 px-2.5 py-1 rounded-sm border border-[#253044] hover:bg-[#1E2738] text-xs"><Download size={12} /> WAV</a>
                      <a data-testid={`job-report-pdf-${j.id}`} href={authedUrl(`/jobs/${j.id}/report.pdf`)} className="flex items-center gap-1 px-2.5 py-1 rounded-sm border border-[#253044] hover:bg-[#1E2738] text-xs"><FileText size={12} /> PDF</a>
                    </>
                  )}
                  {(j.status === "queued" || j.status === "processing") && (
                    <button data-testid={`job-cancel-${j.id}`} onClick={() => onAction("cancel", j)} className="flex items-center gap-1 px-2.5 py-1 rounded-sm border border-[#253044] hover:bg-[#1E2738] text-xs"><XCircle size={12} /> Cancel</button>
                  )}
                  {(j.status === "failed" || j.status === "cancelled") && (
                    <button data-testid={`job-retry-${j.id}`} onClick={() => onAction("retry", j)} className="flex items-center gap-1 px-2.5 py-1 rounded-sm border border-[#253044] hover:bg-[#1E2738] text-xs"><RotateCcw size={12} /> Retry</button>
                  )}
                  {j.status !== "processing" && (
                    <button data-testid={`job-delete-${j.id}`} onClick={() => onAction("delete", j)} className="p-1.5 rounded-sm border border-[#253044] hover:bg-rose-500/10 hover:text-rose-400 text-slate-400"><Trash2 size={13} /></button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
